import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as api from "../api/WatchlistApi";
import type { WatchlistPost } from "../api/WatchlistApi";

interface PostFilters {
  target_id?: number;
  status?: string;
  page: number;
  page_size: number;
}

interface WatchlistContextValue {
  posts: WatchlistPost[];
  postsLoading: boolean;
  error: string;
  postFilters: PostFilters;
  totalPosts: number;
  totalPostPages: number;

  setPostFilters: (f: Partial<PostFilters>) => void;
  loadPosts: (f?: Partial<PostFilters>) => Promise<void>;

  updatePostStatus: (id: number, status: "new" | "relevant" | "archived") => Promise<void>;
  deletePost: (id: number) => Promise<string | null>;
}

const WatchlistContext = createContext<WatchlistContextValue | null>(null);

const DEFAULT_POST_FILTERS: PostFilters = { page: 1, page_size: 20 };

export function WatchlistProvider({ children }: { children: React.ReactNode }) {
  const [posts, setPosts] = useState<WatchlistPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [error, setError] = useState("");
  const [postFilters, setPostFiltersState] = useState<PostFilters>(DEFAULT_POST_FILTERS);
  const [totalPosts, setTotalPosts] = useState(0);
  const [totalPostPages, setTotalPostPages] = useState(1);

  const setPostFilters = useCallback((f: Partial<PostFilters>) => {
    setPostFiltersState((prev) => ({ ...prev, ...f }));
  }, []);

  const loadPosts = useCallback(async (f?: Partial<PostFilters>) => {
    setPostsLoading(true);
    try {
      const merged = { ...postFilters, ...f };
      const data = await api.listPosts(merged);
      setPosts(data.items);
      setTotalPosts(data.total_count);
      setTotalPostPages(data.total_pages);
    } catch {
      setError("Failed to load posts");
    } finally {
      setPostsLoading(false);
    }
  }, [postFilters]);

  useEffect(() => { loadPosts(); }, [postFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const updatePostStatus = useCallback(async (id: number, status: "new" | "relevant" | "archived") => {
    await api.updatePostStatus(id, status);
    setPosts((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
  }, []);

  const deletePost = useCallback(async (id: number): Promise<string | null> => {
    try {
      await api.deletePost(id);
    } catch (err: any) {
      return err?.response?.data?.detail ?? "Failed to delete post";
    }
    setPosts((prev) => prev.filter((p) => p.id !== id));
    setTotalPosts((prev) => prev - 1);
    return null;
  }, []);

  const value = useMemo<WatchlistContextValue>(() => ({
    posts, postsLoading, error,
    postFilters, totalPosts, totalPostPages,
    setPostFilters, loadPosts,
    updatePostStatus, deletePost,
  }), [
    posts, postsLoading, error,
    postFilters, totalPosts, totalPostPages,
    setPostFilters, loadPosts,
    updatePostStatus, deletePost,
  ]);

  return <WatchlistContext.Provider value={value}>{children}</WatchlistContext.Provider>;
}

export function useWatchlist(): WatchlistContextValue {
  const ctx = useContext(WatchlistContext);
  if (!ctx) throw new Error("useWatchlist must be used within WatchlistProvider");
  return ctx;
}
